import { useState, useCallback, useMemo } from "react";
import axiosInstance from "../services/axiosInstance";
import { useQuery } from "@tanstack/react-query";

export interface Patient {
    id: number;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    createdAt: string;
    lastConsultationDate?: string;
    hasConsultation: boolean;
    unpaidAmount?: number;
}

interface PatientFilters {
    searchQuery: string;
    consultationStatus: 'all' | 'consulted' | 'not-consulted';
    page: number;
    size: number;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
}

const DEFAULT_FILTERS: PatientFilters = {
    searchQuery: '',
    consultationStatus: 'all',
    page: 0,
    size: 10,
    sortBy: 'createdAt',
    sortDirection: 'desc'
};

export const usePatientList = () => {
    const [filters, setFilters] = useState<PatientFilters>(DEFAULT_FILTERS);

    const { data, isLoading: loading, error: queryError, refetch } = useQuery({
        queryKey: ['patients', filters],
        queryFn: async () => {
            let url = `/patients/search?query=${encodeURIComponent(filters.searchQuery)}&page=${filters.page}&size=${filters.size}&sort=${filters.sortBy},${filters.sortDirection}`;

            if (filters.consultationStatus !== 'all') {
                url += `&hasConsultation=${filters.consultationStatus === 'consulted'}`;
            }

            const response = await axiosInstance.get(url);
            return response.data;
        },
    });

    const updateFilters = useCallback((newFilters: Partial<PatientFilters>) => {
        setFilters(prev => ({
            ...prev,
            ...newFilters,
            // Reset to page 0 if search or status changes
            page: (newFilters.searchQuery !== undefined || newFilters.consultationStatus !== undefined) ? 0 : (newFilters.page ?? prev.page)
        }));
    }, []);

    const patients = useMemo(() => data?.content || [], [data]);
    const totalPages = data?.totalPages || 0;
    const totalElements = data?.totalElements || 0;

    const error = queryError ? (queryError as any).message : null;

    return {
        patients,
        loading,
        error,
        totalPages,
        totalElements,
        filters,
        setFilters: updateFilters,
        refetch
    };
};
